
/**
 * AccountStorage — referans-parite (features/auth/state/AccountStorage.ts) portu.
 *
 * Hesap-başına IndexedDB anlık-görüntü (snapshot) kasası: token çifti + cache'lenmiş
 * profil (ad/avatar) + presence-niyeti + o hesabın "yönetilen" (account-scoped)
 * localStorage anahtarlarının (taslaklar, arama geçmişi, sohbet/tercih store'ları vs.)
 * tam kopyası. `AccountManagerStore` bunu login/switchAccount/removeAccount
 * akışlarında kullanarak hesaplar arasında geçişte localStorage'ı DOĞRU hesabın
 * verisiyle değiştirir (kaynak-referans `restoreAccountData` → `applyManagedStorageSnapshot`).
 *
 * TEBHOS uyarlamaları (kaynak-referanstan bilinçli farklar):
 *   - `RuntimeConfigSnapshot` / çoklu-instance federasyon alanı YOK — TEBHOS
 *     tek-instance (kendi barındırılan tek sunucu); `instance` alanı portlanmadı.
 *   - Tek `token` yerine `accessToken`+`refreshToken` çifti — AccountManagerStore'un
 *     `AccountEntry` şekliyle birebir (OPAK-OTURUM sonrası `refreshToken`
 *     çoğunlukla "" — bkz. lib/api/client.ts token yorumları).
 *   - Yönetilen-anahtar tespiti kaynak-referansın literal `mobx|mobx-persist|persist|kaynak`
 *     önek-regex'i YERİNE TEBHOS'un gerçek `makePersistent` anahtar envanterine
 *     göre kuruldu (bkz. `isManagedKey` yorumu) — kaynak-referansın kodu kopyalanmadı,
 *     DAVRANIŞI (hesap-başına local-pref izolasyonu) portlandı.
 *   - `_themeManager.ts` / `PersistentStorage.ts` PRESERVED_RESET+clearExcept
 *     bağlantıları (A-paketi ajanı) ve `SyncedPreferencesEngine`/`SyncedField`
 *     (C1 ajanı) dosyalarına DOKUNULMADI; bu modül onların anahtar adlarını
 *     bilmeden çalışır — kendi bağımsız yönetilen-anahtar listesine sahiptir.
 */

import {
  LEGACY_STORE_KEY_RENAMES,
  maybeUnwrapZustandEnvelope,
} from "@app/features/platform/state/PersistedStorageMigration";
import {getProtectedIndexedDB, getProtectedLocalStorage} from "@app/features/platform/state/ProtectedWebStorage";
import {Logger} from "@app/features/platform/utils/Logger";
import type {AccountPresenceIntent} from "@app/features/presence/state/LocalPresence";

const logger = new Logger("AccountStorage");
const DB_NAME = "TebhosAccounts";
const DB_VERSION = 1;
const STORE_NAME = "accounts";
const browserIndexedDB = getProtectedIndexedDB();
const browserLocalStorage = getProtectedLocalStorage();

export interface AccountUserData {
  name: string | null;
  imageUrl: string | null;
}

export type {AccountPresenceIntent};

export interface StoredAccount {
  userId: string;
  accessToken: string | null;
  refreshToken: string | null;
  userData?: AccountUserData;
  presenceIntent?: AccountPresenceIntent | null;
  managedStorageData: Record<string, string>;
  lastActive: number;
  isValid?: boolean;
}

type IdbOpenState = "idle" | "opening" | "open" | "failed";

/**
 * Hesabın kendi kasa-anahtarı — ASLA snapshot'a dahil edilmez (kendini
 * ezmesin: switchAccount sırasında accounts[]/currentAccountId localStorage'da
 * SABİT kalmalı, snapshot yalnız DİĞER yerel tercihleri taşır).
 */
const ACCOUNT_VAULT_KEY = "tebhos-account-manager";

/**
 * Cihaz-seviyeli (hesaptan BAĞIMSIZ kalması gereken) store'lar — Discord/kaynak-referansta
 * da bunlar tarayıcıya/cihaza aittir, hesaba değil (erişilebilirlik, geliştirici
 * modu, mobil-layout genişliği, klavye-modu tanıtım balonu).
 */
const MANAGED_KEY_EXCLUDE: ReadonlySet<string> = new Set([
  ACCOUNT_VAULT_KEY,
  "Accessibility",
  "DeveloperMode",
  "DeveloperOptions",
  "MobileLayout",
  "KeyboardModeIntro",
]);

/**
 * TEBHOS'ta yeni store'ların çoğunluğu `makePersistent(this, "tebhos-...", ...)`
 * konvansiyonuyla yazılıyor (bkz. MobXPersistence.ts çağrı-siteleri — 15+ örnek).
 * Bu önekli HER anahtar otomatik yönetilir (yeni eklenen store'lar da otomatik
 * kapsanır) — istisna yalnız hesap kasasının kendisi (yukarıda hariç tutuldu).
 */
function hasManagedPrefix(key: string): boolean {
  return key.startsWith("tebhos-") || key.startsWith("tebhos:");
}

/**
 * Daha eski bir grup store PascalCase çıplak isimle yazıyor (önek yok) ama
 * hesap-anlamlı (taslak/geçmiş/tercih) içerik taşıyor — bunlar açık listeyle
 * kapsama alınır. Yeni bir hesap-anlamlı store eklenirse ya "tebhos-" öneki
 * alır (otomatik kapsanır) ya da bu listeye eklenmelidir.
 */
const MANAGED_KEY_EXACT_INCLUDE: ReadonlySet<string> = new Set([
  "SelectedGuild",
  "SelectedChannel",
  "SearchHistory",
  "EmojiPicker",
  "Inbox",
  "MemberList",
  "channel-notifications",
  "dm-profile-panel",
  "StreamAudioPrefs",
  "EntranceSoundListenerPrefs",
  "EmojiRegistry",
  // Zustand-temizliği (2026-07-18): taslak anahtarı kaynak-referans-hizalı "Drafts"
  // oldu ("tebhos-chat-drafts" → boot migrasyonu) — taslaklar hesap-anlamlı.
  "Drafts",
]);

function isManagedKey(key: string): boolean {
  if (!key || MANAGED_KEY_EXCLUDE.has(key)) {
    return false;
  }
  if (hasManagedPrefix(key)) {
    return true;
  }
  return MANAGED_KEY_EXACT_INCLUDE.has(key);
}

function collectManagedKeys(storage: Storage): Array<string> {
  const keys: Array<string> = [];
  const total = storage.length;
  for (let i = 0; i < total; i++) {
    const key = storage.key(i);
    if (key && isManagedKey(key)) {
      keys.push(key);
    }
  }
  return keys;
}

function stableNow(): number {
  return Date.now();
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    const timeout = new Promise<T>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error(`Timeout: ${label} (${ms}ms)`)), ms);
    });
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== null) {
      clearTimeout(timer);
    }
  }
}

class AccountStorage {
  private db: IDBDatabase | null = null;
  private openPromise: Promise<IDBDatabase | null> | null = null;
  private openState: IdbOpenState = "idle";
  private memoryCache = new Map<string, StoredAccount>();
  private storageSwapTail: Promise<void> = Promise.resolve();

  private enqueueStorageSwap(fn: () => Promise<void>): Promise<void> {
    const run = async (): Promise<void> => {
      await fn();
    };
    const next = this.storageSwapTail.then(run, run);
    this.storageSwapTail = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  async init(): Promise<void> {
    if (!browserIndexedDB) {
      return;
    }
    if (this.openState === "open") {
      return;
    }
    if (this.openState === "opening" && this.openPromise) {
      await this.openPromise;
      return;
    }
    this.openState = "opening";
    this.openPromise = new Promise<IDBDatabase | null>((resolve, reject) => {
      const request = browserIndexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        this.openState = "failed";
        logger.error("IndexedDB açılamadı", request.error);
        reject(request.error ?? new Error("IndexedDB open error"));
      };
      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, {keyPath: "userId"}).createIndex("lastActive", "lastActive");
          logger.debug("Hesaplar için IndexedDB object store oluşturuldu");
        }
      };
      request.onsuccess = () => {
        this.db = request.result;
        this.openState = "open";
        resolve(this.db);
      };
    });
    try {
      await withTimeout(this.openPromise, 5000, "IndexedDB open");
    } finally {
      this.openPromise = null;
    }
  }

  private async ensureDb(): Promise<void> {
    if (!browserIndexedDB) {
      return;
    }
    if (!this.db) {
      try {
        await this.init();
      } catch (err) {
        logger.warn("IndexedDB init başarısız; bellek-içi fallback kullanılıyor", err);
      }
    }
  }

  private captureManagedStorageSnapshot(): Record<string, string> {
    if (!browserLocalStorage) {
      return {};
    }
    const snapshot: Record<string, string> = {};
    for (const key of collectManagedKeys(browserLocalStorage)) {
      const value = browserLocalStorage.getItem(key);
      if (value != null) {
        snapshot[key] = value;
      }
    }
    return snapshot;
  }

  private async applyManagedStorageSnapshot(snapshot: Record<string, string>): Promise<void> {
    if (!browserLocalStorage) {
      return;
    }
    const keysToRemove = collectManagedKeys(browserLocalStorage).filter((key) => snapshot[key] === undefined);
    for (const [key, value] of Object.entries(snapshot)) {
      try {
        browserLocalStorage.setItem(key, value);
      } catch (err) {
        logger.warn(`Yönetilen localStorage anahtarı "${key}" yazılamadı`, err);
      }
    }
    for (const key of keysToRemove) {
      try {
        browserLocalStorage.removeItem(key);
      } catch (err) {
        logger.warn(`Yönetilen localStorage anahtarı "${key}" silinemedi`, err);
      }
    }
  }

  /**
   * Snapshot'ı güvenli kopyalarken migrasyon-öncesi kayıtları da normalize
   * eder: IndexedDB'de zustand-dönemi anlık-görüntüler eski anahtar adı
   * ("tebhos-chat-drafts") ve/veya sarmalı format içerebilir — restore
   * edildiğinde localStorage'a GÜNCEL ad + düz formatla yazılmalı (aksi halde
   * hesap değişimi migrasyon-öncesi durumu geri getirirdi; boot migrasyonu
   * "bir kez koştu" bayrağı nedeniyle bir daha düzeltmez).
   */
  private cloneStorageSnapshot(snapshot: Record<string, string>): Record<string, string> {
    const safe: Record<string, string> = {};
    for (const [key, value] of Object.entries(snapshot)) {
      if (value == null) {
        continue;
      }
      const raw = typeof value === "string" ? value : String(value);
      const targetKey = LEGACY_STORE_KEY_RENAMES[key] ?? key;
      if (targetKey !== key && snapshot[targetKey] != null) {
        // Yeni anahtar zaten snapshot'ta — o kazanır, eski girdi düşer.
        continue;
      }
      safe[targetKey] = maybeUnwrapZustandEnvelope(raw) ?? raw;
    }
    return safe;
  }

  private cloneUserData(userData?: AccountUserData): AccountUserData | undefined {
    if (!userData) {
      return undefined;
    }
    return {...userData};
  }

  private clonePresenceIntent(intent?: AccountPresenceIntent | null): AccountPresenceIntent | null | undefined {
    if (intent === undefined) {
      return undefined;
    }
    if (intent === null) {
      return null;
    }
    return {
      status: intent.status,
      statusResetsAt: intent.statusResetsAt ?? null,
      statusResetsTo: intent.statusResetsTo ?? null,
      customStatus: intent.customStatus ? {...intent.customStatus} : null,
      capturedAt: intent.capturedAt,
    };
  }

  private normalizeRecord(record: StoredAccount): StoredAccount {
    const managed = record.managedStorageData ?? {};
    const managedSnapshot = this.cloneStorageSnapshot(managed);
    return {
      userId: record.userId,
      accessToken: record.accessToken,
      refreshToken: record.refreshToken,
      userData: this.cloneUserData(record.userData),
      presenceIntent: this.clonePresenceIntent(record.presenceIntent),
      managedStorageData: managedSnapshot,
      lastActive: record.lastActive,
      isValid: record.isValid,
    };
  }

  private sanitizeRecord(record: StoredAccount): StoredAccount {
    return this.normalizeRecord(record);
  }

  private isDataCloneError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }
    return (error as {name?: unknown}).name === "DataCloneError";
  }

  private isInvalidStateError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }
    return (error as {name?: unknown}).name === "InvalidStateError";
  }

  /**
   * Aktif hesabın hem token çiftini hem de O ANKİ yönetilen-localStorage
   * anlık-görüntüsünü IndexedDB'ye kaydeder (kaynak-referans `stashAccountData` 1-1).
   * `accessToken` zorunlu — token yoksa yazılabilir bir hesap yok demektir.
   */
  async stashAccountData(
    userId: string,
    accessToken: string | null,
    refreshToken: string | null,
    userData?: AccountUserData,
    presenceIntent?: AccountPresenceIntent | null,
  ): Promise<void> {
    if (!userId) {
      const error = new Error("Geçersiz stashAccountData: userId eksik");
      logger.error("stashAccountData için geçersiz parametreler", error);
      throw error;
    }
    await this.ensureDb();
    const existing = !userData || presenceIntent === undefined ? await this.getRecord(userId) : null;
    const resolvedUserData = userData ?? existing?.userData;
    const resolvedPresenceIntent = presenceIntent === undefined ? (existing?.presenceIntent ?? undefined) : presenceIntent;
    const managedStorageData = this.captureManagedStorageSnapshot();
    const record: StoredAccount = {
      userId,
      accessToken,
      refreshToken,
      userData: resolvedUserData,
      presenceIntent: resolvedPresenceIntent,
      managedStorageData,
      lastActive: stableNow(),
    };
    const safeRecord = this.sanitizeRecord(record);
    try {
      if (!this.db) {
        this.memoryCache.set(userId, safeRecord);
        logger.debug(`${userId} için hesap verisi stash edildi (bellek fallback)`);
        return;
      }
      await withTimeout(
        new Promise<void>((resolve, reject) => {
          const tx = this.db!.transaction([STORE_NAME], "readwrite");
          const store = tx.objectStore(STORE_NAME);
          const req = store.put(safeRecord);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error ?? new Error("IndexedDB put failed"));
        }),
        5000,
        "IndexedDB put account",
      );
      logger.debug(`${userId} için hesap verisi stash edildi (idb)`);
    } catch (err) {
      if (this.isDataCloneError(err) || this.isInvalidStateError(err)) {
        logger.warn(`Hesap ${userId} stash edilirken IDB hatası; bellek fallback kullanılıyor`, err);
        this.memoryCache.set(userId, safeRecord);
        return;
      }
      logger.error(`${userId} için hesap verisi stash edilemedi`, err);
      throw err;
    }
  }

  /**
   * Bir hesabın kayıtlı verisini döndürür VE localStorage'ı onun yönetilen
   * anlık-görüntüsüyle değiştirir (kaynak-referans `restoreAccountData` 1-1).
   */
  async restoreAccountData(userId: string): Promise<StoredAccount | null> {
    if (!userId) {
      return null;
    }
    await this.ensureDb();
    const record = await this.getRecord(userId);
    if (!record) {
      return null;
    }
    const normalized = this.normalizeRecord(record);
    await this.enqueueStorageSwap(async () => {
      await this.applyManagedStorageSnapshot(normalized.managedStorageData ?? {});
    });
    await this.updateLastActive(userId);
    logger.debug(`${userId} için hesap verisi geri yüklendi`);
    return normalized;
  }

  async getAllAccounts(): Promise<Array<StoredAccount>> {
    await this.ensureDb();
    try {
      if (!this.db) {
        return Array.from(this.memoryCache.values()).map((r) => this.normalizeRecord(r));
      }
      const records = await withTimeout(
        new Promise<Array<StoredAccount>>((resolve, reject) => {
          const tx = this.db!.transaction([STORE_NAME], "readonly");
          const store = tx.objectStore(STORE_NAME);
          const req = store.getAll();
          req.onsuccess = () => resolve((req.result as Array<StoredAccount>) ?? []);
          req.onerror = () => reject(req.error ?? new Error("IndexedDB getAll failed"));
        }),
        5000,
        "IndexedDB getAll accounts",
      );
      return records.map((r) => this.normalizeRecord(r));
    } catch (err) {
      logger.error("Kayıtlı hesaplar okunamadı", err);
      return Array.from(this.memoryCache.values()).map((r) => this.normalizeRecord(r));
    }
  }

  async deleteAccount(userId: string): Promise<void> {
    await this.ensureDb();
    if (!userId) {
      return;
    }
    try {
      if (!this.db) {
        this.memoryCache.delete(userId);
        return;
      }
      await withTimeout(
        new Promise<void>((resolve, reject) => {
          const tx = this.db!.transaction([STORE_NAME], "readwrite");
          const store = tx.objectStore(STORE_NAME);
          const req = store.delete(userId);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error ?? new Error("IndexedDB delete failed"));
        }),
        5000,
        "IndexedDB delete account",
      );
      logger.debug(`${userId} için hesap verisi silindi`);
    } catch (err) {
      logger.error(`${userId} için hesap verisi silinemedi`, err);
      throw err;
    }
  }

  async updateAccountUserData(userId: string, userData: AccountUserData): Promise<void> {
    await this.ensureDb();
    if (!userId) {
      return;
    }
    try {
      const record = await this.getRecord(userId);
      if (!record) {
        return;
      }
      await this.putRecord({...record, userData});
    } catch (err) {
      logger.error(`${userId} hesabı için userData güncellenemedi`, err);
    }
  }

  async updateAccountValidity(userId: string, isValid: boolean): Promise<void> {
    await this.ensureDb();
    if (!userId) {
      return;
    }
    try {
      const record = await this.getRecord(userId);
      if (!record) {
        return;
      }
      await this.putRecord({...record, isValid});
    } catch (err) {
      logger.error(`${userId} hesabı için geçerlilik güncellenemedi`, err);
    }
  }

  private async getRecord(userId: string): Promise<StoredAccount | null> {
    if (!userId) {
      return null;
    }
    if (!this.db) {
      return this.memoryCache.get(userId) ?? null;
    }
    try {
      return await withTimeout(
        new Promise<StoredAccount | null>((resolve, reject) => {
          const tx = this.db!.transaction([STORE_NAME], "readonly");
          const store = tx.objectStore(STORE_NAME);
          const req = store.get(userId);
          req.onsuccess = () => resolve((req.result as StoredAccount | undefined) ?? null);
          req.onerror = () => reject(req.error ?? new Error("IndexedDB get failed"));
        }),
        5000,
        "IndexedDB get account",
      );
    } catch (err) {
      if (this.isInvalidStateError(err)) {
        logger.warn(`${userId} için getRecord sırasında InvalidStateError; bellek fallback`, err);
        return this.memoryCache.get(userId) ?? null;
      }
      throw err;
    }
  }

  private async putRecord(record: StoredAccount): Promise<void> {
    const normalized = this.normalizeRecord(record);
    if (!this.db) {
      this.memoryCache.set(record.userId, normalized);
      return;
    }
    try {
      await withTimeout(
        new Promise<void>((resolve, reject) => {
          const tx = this.db!.transaction([STORE_NAME], "readwrite");
          const store = tx.objectStore(STORE_NAME);
          const req = store.put(normalized);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error ?? new Error("IndexedDB put failed"));
        }),
        5000,
        "IndexedDB put account record",
      );
      this.memoryCache.set(record.userId, normalized);
    } catch (err) {
      if (this.isInvalidStateError(err)) {
        logger.warn(`${record.userId} için putRecord sırasında InvalidStateError; bellek fallback`, err);
        this.memoryCache.set(record.userId, normalized);
        return;
      }
      throw err;
    }
  }

  private async updateLastActive(userId: string): Promise<void> {
    const record = await this.getRecord(userId);
    if (!record) {
      return;
    }
    await this.putRecord({...record, lastActive: stableNow()});
  }
}

export default new AccountStorage();
